// ═══════════════════════════════════════════════════════════════════════════
// DebugFAB — botón para reportar bugs, sólo con sesión iniciada (19-may-2026)
// ───────────────────────────────────────────────────────────────────────────
// 'inline': pie de la barra lateral (toda la app) · 'fab': flotante, vistas previas sin barra.
// Click → modal con:
//   - Captura automática de la pantalla actual (html-to-image)
//   - Preview de la imagen
//   - Form: descripción, severidad, categoría
//   - Submit → crearReporte() en debugReports lib
//
// Anti self-capture: el FAB se oculta antes del snapshot y se restaura después.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '@core/contexts/AuthContext'
import { useToast } from './Toast'
import {
  crearReporte,
  capturarContexto,
  validarImagenAdjunta,
  SEVERIDADES,
  SEVERIDAD_LABELS,
  CATEGORIAS,
  CATEGORIA_LABELS,
  TIPOS,
  TIPO_LABELS,
  tipoDefault,
} from '@modules/debugging/lib/debugReports'
import { ACCEPT_IMAGENES } from '@core/lib/archivosSubibles'

export default function DebugFAB({ variant = 'fab' }) {
  const { user } = useAuth()
  const isInline = variant === 'inline'
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [capturando, setCapturando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [screenshot, setScreenshot] = useState(null) // { blob, dataUrl, contentType, origen: 'auto'|'subida' }
  const [descripcion, setDescripcion] = useState('')
  const [severidad, setSeveridad] = useState(SEVERIDADES.MEDIA)
  const [categoria, setCategoria] = useState(CATEGORIAS.UI)
  const [tipo, setTipo] = useState(TIPOS.BUG) // bug | funcionalidad (KxKGNoPa)
  const [errorCaptura, setErrorCaptura] = useState(null)
  const fabRef = useRef(null)
  const fileInputRef = useRef(null) // kUlsHDcy: subir un print propio

  // No renderizar si no hay user autenticado.
  if (!user) return null

  // ─── Captura ────────────────────────────────────────────────────────────
  // Estrategia tolerante: si html-to-image falla (típicamente por imágenes
  // cross-origin sin CORS headers, fonts externas, o iframes sandboxed), no
  // bloqueamos el reporte. El user puede enviar sin screenshot y describir
  // con palabras. El error real se loggea para diagnóstico.
  //
  // Placeholder transparente 1x1 para imágenes que no se pueden cargar
  // (en vez de tirar Event de error que rompe toda la captura).
  const IMG_PLACEHOLDER = 'data:image/svg+xml;charset=utf-8,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%221%22 height%3D%221%22%2F%3E'

  const stringifyError = (err) => {
    if (!err) return 'Error desconocido'
    if (typeof err === 'string') return err
    if (err.message) return err.message
    if (err instanceof Event) {
      const target = err.target
      if (target?.src) return `Error cargando recurso: ${target.src.slice(0, 100)}`
      if (target?.href) return `Error cargando recurso: ${target.href.slice(0, 100)}`
      return 'Error de carga de recurso externo (CORS o 404)'
    }
    try { return JSON.stringify(err).slice(0, 200) } catch { return String(err) }
  }

  const capturarPantalla = async () => {
    setCapturando(true)
    setErrorCaptura(null)
    try {
      // Ocultar el FAB y el propio modal para que NO se capturen.
      if (fabRef.current) fabRef.current.style.visibility = 'hidden'
      const modales = document.querySelectorAll('[data-debug-modal]')
      modales.forEach(m => { m.style.visibility = 'hidden' })

      const htmlToImage = await import('html-to-image')
      const opciones = {
        cacheBust: false,       // no agregar query strings (rompe CORS más)
        pixelRatio: 1,           // 1x para evitar OOM en Retina
        quality: 0.85,
        skipFonts: true,         // evita fetch de Google Fonts
        imagePlaceholder: IMG_PLACEHOLDER,  // imágenes que fallen → 1px transparente
        filter: (node) => {
          // Saltear elementos problemáticos
          if (node.tagName === 'IFRAME') return false
          if (node.tagName === 'SCRIPT') return false
          // Saltear imágenes cross-origin que no tienen CORS habilitado
          // (avatares Google, recaptcha, etc.)
          if (node.tagName === 'IMG' && node.src) {
            try {
              const u = new URL(node.src, window.location.href)
              const sameOrigin = u.origin === window.location.origin
              const isDataUrl = u.protocol === 'data:'
              const isBlobUrl = u.protocol === 'blob:'
              if (!sameOrigin && !isDataUrl && !isBlobUrl) return false
            } catch { return false }
          }
          return true
        },
      }
      // toBlob directo (sin pasar por dataUrl + fetch que falla por CSP).
      const blob = await htmlToImage.toBlob(document.body, opciones)
      if (!blob) throw new Error('Captura vacía (toBlob devolvió null)')
      // Generamos dataUrl SOLO para el preview en el modal (no para fetch).
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('No se pudo leer el blob'))
        reader.readAsDataURL(blob)
      })
      setScreenshot({ blob, dataUrl, contentType: 'image/png', origen: 'auto' })
    } catch (err) {
      console.error('[DebugFAB] captura falló:', err)
      setErrorCaptura(stringifyError(err))
    } finally {
      setCapturando(false)
      if (fabRef.current) fabRef.current.style.visibility = ''
      const modales = document.querySelectorAll('[data-debug-modal]')
      modales.forEach(m => { m.style.visibility = '' })
    }
  }

  // ─── Subir un print propio (kUlsHDcy) ────────────────────────────────────
  // La auto-captura (html-to-image) no toma bien pop-ups del SO ni alertas por
  // mail. El usuario puede subir su propio print, que REEMPLAZA a la captura
  // como imagen principal del reporte (mismo path/lógica de Storage —
  // debugReports/{id}.<ext> — así no hace falta tocar storage.rules).
  const handleSubirImagen = (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // permite re-elegir el mismo archivo
    if (!file) return
    const check = validarImagenAdjunta(file)
    if (!check.ok) {
      toast.error?.(check.error) || alert(check.error)
      return
    }
    const dataUrl = URL.createObjectURL(file)
    setErrorCaptura(null)
    setCapturando(false)
    setScreenshot({ blob: file, dataUrl, contentType: file.type || 'image/png', origen: 'subida' })
  }

  // Revocar el object URL del print subido cuando se reemplaza o se cierra
  // (los dataUrl base64 de la auto-captura no necesitan esto; los blob: sí).
  useEffect(() => {
    const url = screenshot?.dataUrl
    if (url && url.startsWith('blob:')) {
      return () => URL.revokeObjectURL(url)
    }
  }, [screenshot?.dataUrl])

  // Al abrir el modal, capturamos automáticamente
  useEffect(() => {
    if (open && !screenshot && !capturando) {
      // Pequeño delay para que el modal aparezca primero y el state se asiente
      setTimeout(() => { capturarPantalla() }, 150)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleAbrir = () => {
    setDescripcion('')
    setSeveridad(SEVERIDADES.MEDIA)
    setCategoria(CATEGORIAS.UI)
    setTipo(TIPOS.BUG)
    setScreenshot(null)
    setOpen(true)
  }

  // Elegir categoría "Feature request" arrastra el tipo a funcionalidad (y viceversa
  // vuelve a bug) — el usuario puede pisarlo después con el toggle de tipo.
  const handleCategoria = (cat) => {
    setCategoria(cat)
    setTipo(tipoDefault(cat))
  }

  const handleCerrar = () => {
    if (enviando) return
    setOpen(false)
    setScreenshot(null)
    setDescripcion('')
  }

  const handleEnviar = async (e) => {
    e?.preventDefault()
    if (!descripcion.trim()) {
      toast.error?.('Agregá una descripción del problema') || alert('Falta descripción')
      return
    }
    setEnviando(true)
    try {
      const contexto = await capturarContexto({ empresaId: null })
      const { id } = await crearReporte({
        screenshotBlob: screenshot?.blob || null,
        screenshotContentType: screenshot?.contentType || 'image/png',
        descripcion,
        severidad,
        categoria,
        tipo,
        contexto,
        notificarUids: [], // TODO D5 — query admins Milbrands
        // Decisión Mariano 11-ago-2026: TODOS los reportes nacen 'informe' —
        // el fast-path `crearAprobado` quedó eliminado. La única puerta a
        // 'nuevo' es la aprobación explícita (`debugging.autorizar`).
      })
      toast.success?.(`Reporte #${id.slice(0, 6)} enviado`) || alert('Enviado')
      handleCerrar()
    } catch (err) {
      console.error('[DebugFAB] enviar falló:', err)
      toast.error?.('Error al enviar: ' + err.message) || alert('Error: ' + err.message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      {/* ─── Trigger ─── */}
      {/* variant 'inline' → ícono chico del pie de la barra lateral (toda la app).
          variant 'fab' (default) → flotante de las vistas previas sin barra. */}
      <button
        ref={fabRef}
        onClick={handleAbrir}
        title="Reportar bug"
        aria-label="Reportar bug"
        className={isInline
          ? 'flex items-center justify-center w-[26px] h-[26px] rounded-md text-brand-accent bg-brand-accent/10 hover:bg-brand-accent/20 transition-colors'
          : 'fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-brand-accent hover:bg-brand-accent-hover text-black shadow-lg shadow-brand-accent/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95'}
        style={{ display: open ? 'none' : 'flex' }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={isInline ? 'w-[13px] h-[13px]' : 'w-6 h-6'}>
          <path d="M8 2v4M16 2v4M5 8h14a1 1 0 0 1 1 1v3a8 8 0 0 1-16 0V9a1 1 0 0 1 1-1zM3 13l-2 1M21 13l2 1M3 18l-2 1M21 18l2 1M12 12v8" />
        </svg>
      </button>

      {/* ─── Modal ─── */}
      {/* Portal a document.body: el trigger inline vive dentro del <aside>, que
          tiene un transform (translate-x) y por ende crea un containing block
          que atraparía a `position: fixed`. Renderizando el modal por portal
          escapa del sidebar y se centra en el viewport. */}
      {/* YW88y2qu (5-jul): el modal de CREACIÓN sigue el mismo molde visual que
          la vista del reporte en /debugging — captura a la izquierda, datos a
          la derecha (grid 2 col) — y copia el molde de `Modal.jsx`: centrado,
          y la caja con tope de altura (`max-h-[90vh] overflow-y-auto`) es la
          que scrollea. Hasta el 4-oct-2026 iba anclado arriba
          (`items-start pt-[150px]`); report lYmH60J76yzM3pG8VDj7. */}
      {open && createPortal(
        <div
          data-debug-modal
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={handleCerrar}
        >
          <div
            className="bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header — mismo lenguaje del detalle del reporte */}
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">{tipo === TIPOS.FUNCIONALIDAD ? '✨' : '🐛'}</span>
                <h2 className="text-base font-bold text-white">{tipo === TIPOS.FUNCIONALIDAD ? 'Pedir funcionalidad' : 'Reportar bug'}</h2>
                <span className="text-[10.5px] text-zinc-500 hidden sm:inline">— se guarda en /debugging con tu captura y contexto</span>
              </div>
              <button
                onClick={handleCerrar}
                disabled={enviando}
                className="text-zinc-500 hover:text-white transition-colors disabled:opacity-50"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body — grid espejo de DetalleReporte: captura | datos */}
            <div className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Captura (columna izquierda) */}
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2">
                    Captura
                    {screenshot?.origen === 'subida' && (
                      <span className="normal-case tracking-normal text-[10px] px-1.5 py-0.5 rounded border border-brand-accent/30 text-brand-accent bg-brand-accent/10">📎 imagen subida</span>
                    )}
                  </div>
                  <div className="relative bg-black/40 border border-white/10 rounded-lg overflow-hidden">
                    {capturando && (
                      <div className="aspect-video flex items-center justify-center text-zinc-500 text-sm">
                        Capturando pantalla…
                      </div>
                    )}
                    {!capturando && screenshot?.dataUrl && (
                      <img
                        src={screenshot.dataUrl}
                        alt="Captura"
                        className="w-full max-h-96 object-contain"
                      />
                    )}
                    {!capturando && !screenshot && errorCaptura && (
                      <div className="aspect-video flex flex-col items-center justify-center gap-2 px-4 text-center">
                        <span className="text-amber-400 text-sm">⚠️ No se pudo capturar la pantalla</span>
                        <pre className="text-[10.5px] text-zinc-500 max-w-full overflow-x-auto whitespace-pre-wrap break-words">
                          {errorCaptura}
                        </pre>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={capturarPantalla}
                            className="text-brand-accent text-xs hover:underline"
                          >
                            Reintentar
                          </button>
                          <span className="text-zinc-600 text-xs">·</span>
                          <span className="text-zinc-500 text-xs">Podés reportar igual con la descripción.</span>
                        </div>
                      </div>
                    )}
                    {!capturando && !screenshot && !errorCaptura && (
                      <div className="aspect-video flex flex-col items-center justify-center gap-2 text-zinc-500 text-sm">
                        <span>Sin captura</span>
                        <button
                          type="button"
                          onClick={capturarPantalla}
                          className="text-brand-accent text-xs hover:underline"
                        >
                          Reintentar captura
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Toolbar de imagen (kUlsHDcy): subir un print propio / volver
                      a la auto-captura. Útil cuando el bug es un pop-up del SO o
                      una alerta por mail que html-to-image no toma bien. */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_IMAGENES}
                    className="hidden"
                    onChange={handleSubirImagen}
                    disabled={enviando}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={enviando || capturando}
                      className="text-brand-accent hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      📎 Subir una imagen propia
                    </button>
                    {screenshot?.origen === 'subida' && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <button
                          type="button"
                          onClick={capturarPantalla}
                          disabled={enviando || capturando}
                          className="text-zinc-400 hover:text-white disabled:opacity-50"
                        >
                          Volver a la auto-captura
                        </button>
                      </>
                    )}
                    <span className="text-zinc-600 w-full sm:w-auto">
                      Ideal para pop-ups del sistema o alertas por mail (PNG/JPG, máx 5 MB).
                    </span>
                  </div>

                  {/* Info auto-capturada */}
                  <div className="mt-3 text-[10.5px] text-zinc-600 bg-black/30 rounded-lg p-3 leading-relaxed">
                    Se captura automáticamente: URL actual, tamaño de ventana, browser, usuario y los últimos 5 errores JS de tu sesión. Esto nos ahorra preguntarte cosas extra.
                  </div>
                </div>

                {/* Datos (columna derecha) */}
                <div className="space-y-4">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2">Descripción *</div>
                    <textarea
                      value={descripcion}
                      onChange={e => setDescripcion(e.target.value)}
                      rows={5}
                      placeholder="Describí brevemente el problema. Ej: 'Al hacer click en X, aparece Y en vez de Z.'"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-brand-accent resize-none"
                      disabled={enviando}
                    />
                  </div>

                  {/* Tipo: ¿bug o pedido de funcionalidad? (reporte KxKGNoPa) */}
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2">¿Qué es?</div>
                    <div className="flex rounded-lg bg-zinc-800 border border-white/10 overflow-hidden w-fit">
                      {Object.entries(TIPO_LABELS).map(([key, info]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setTipo(key)}
                          disabled={enviando}
                          className={`px-3 py-1.5 text-xs transition-colors ${tipo === key ? 'bg-brand-accent/80 text-white font-semibold' : 'text-zinc-400 hover:text-white'}`}
                        >
                          {info.emoji} {info.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Severidad + Categoría */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2">Severidad</div>
                      <select
                        value={severidad}
                        onChange={e => setSeveridad(e.target.value)}
                        className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-accent"
                        disabled={enviando}
                      >
                        {Object.entries(SEVERIDAD_LABELS).map(([key, info]) => (
                          <option key={key} value={key}>
                            {info.emoji} {info.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2">Categoría</div>
                      <select
                        value={categoria}
                        onChange={e => handleCategoria(e.target.value)}
                        className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-accent"
                        disabled={enviando}
                      >
                        {Object.entries(CATEGORIA_LABELS).map(([key, info]) => (
                          <option key={key} value={key}>
                            {info.emoji} {info.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2">
              <button
                onClick={handleCerrar}
                disabled={enviando}
                className="px-4 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-zinc-300 hover:bg-white/[0.1] transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleEnviar}
                disabled={enviando || capturando || !descripcion.trim()}
                className="px-4 py-2 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-black text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enviando ? 'Enviando…' : (tipo === TIPOS.FUNCIONALIDAD ? 'Pedir funcionalidad' : 'Reportar bug')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
